import { useState } from 'react';
import { ProjectStatus } from '../../graphql/types';
import { useTeams } from '../../graphql/queries';
import { TextField, SelectField } from '../forms';
import type { CreateProjectInput, UpdateProjectInput } from '../../lib/apiClient';
import '@tylertech/forge/button';

export interface ProjectFormData {
  name: string;
  description: string;
  status: ProjectStatus;
  ownerDivision: string;
  ownerTeamId: string;
  startDate: string;
  targetEndDate: string;
}

interface ProjectFormProps {
  mode: 'create' | 'edit';
  initialData?: Partial<ProjectFormData>;
  onSubmit: (data: CreateProjectInput | UpdateProjectInput) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const ForgeButton = 'forge-button' as React.ElementType;

export function ProjectForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
}: ProjectFormProps): JSX.Element {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    status: initialData?.status ?? ProjectStatus.Planning,
    ownerDivision: initialData?.ownerDivision ?? '',
    ownerTeamId: initialData?.ownerTeamId ?? '',
    startDate: initialData?.startDate ?? '',
    targetEndDate: initialData?.targetEndDate ?? '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const { data: teamsData, loading: teamsLoading } = useTeams();
  const teams = teamsData?.teams.edges.map((edge) => edge.node) ?? [];

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.ownerDivision.trim()) errors.ownerDivision = 'Division is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validate()) return;
    const input: CreateProjectInput = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      status: formData.status,
      ownerDivision: formData.ownerDivision.trim(),
      ownerTeamId: formData.ownerTeamId || undefined,
      startDate: formData.startDate || undefined,
      targetEndDate: formData.targetEndDate || undefined,
    };
    await onSubmit(input);
  };

  const set =
    (key: keyof ProjectFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      setFormData((p) => ({ ...p, [key]: e.target.value }));
    };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate>
      <TextField
        name="proj-name"
        label="Name"
        required
        placeholder="e.g., Tyler Connect Migration"
        helperText="A descriptive name for the project"
        value={formData.name}
        onChange={set('name')}
        error={
          validationErrors.name ? { type: 'required', message: validationErrors.name } : undefined
        }
      />

      <TextField
        name="proj-description"
        label="Description"
        placeholder="Brief description of the project"
        helperText="Optional description"
        value={formData.description}
        onChange={set('description')}
      />

      <SelectField
        name="proj-status"
        label="Status"
        required
        value={formData.status}
        onChange={set('status')}
        options={[
          { value: ProjectStatus.Planning, label: 'Planning' },
          { value: ProjectStatus.Active, label: 'Active' },
          { value: ProjectStatus.OnHold, label: 'On Hold' },
          { value: ProjectStatus.Complete, label: 'Complete' },
          { value: ProjectStatus.Archived, label: 'Archived' },
        ]}
      />

      <TextField
        name="proj-division"
        label="Owner Division"
        required
        placeholder="e.g., DSD"
        helperText="Division responsible for this project"
        value={formData.ownerDivision}
        onChange={set('ownerDivision')}
        error={
          validationErrors.ownerDivision
            ? { type: 'required', message: validationErrors.ownerDivision }
            : undefined
        }
      />

      <SelectField
        name="proj-team"
        label="Owner Team"
        placeholder="Select a team (optional)"
        value={formData.ownerTeamId}
        onChange={set('ownerTeamId')}
        disabled={teamsLoading}
        helperText="Team responsible for this project"
        options={teams.map((t) => ({ value: t.entityId, label: t.name }))}
      />

      <TextField
        name="proj-start"
        label="Start Date"
        type="date"
        value={formData.startDate}
        onChange={set('startDate')}
      />

      <TextField
        name="proj-end"
        label="Target End Date"
        type="date"
        value={formData.targetEndDate}
        onChange={set('targetEndDate')}
      />

      <div className="form-actions">
        <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
          {mode === 'create' ? 'Create Project' : 'Save Changes'}
        </ForgeButton>
        <ForgeButton type="button" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </ForgeButton>
      </div>
    </form>
  );
}
